import React from 'react';
import { cn, type TestIdProps } from '../utils';
import { Button } from './Button';
import { Dropdown } from './Dropdown';
import { Icon } from './icons/Icon';
import { type IconName } from '../icons/names';

type BaseHeaderNavigationAttributes = Pick<React.HTMLAttributes<HTMLElement>, 'className'>;

/** Descriptor for a single action rendered inside the mobile overflow dropdown. */
export interface NavAction {
  /** Visible label of the action */
  label: string;
  /** Optional icon displayed before the label */
  icon?: IconName;
  /** Click handler */
  onClick: () => void;
  /** Whether the action is disabled */
  disabled?: boolean;
  /** Shown on hover — e.g. why a disabled action cannot be used right now */
  tooltip?: string;
  /** Extra class names applied to the Dropdown.Item (e.g. `'text-error-text'`) */
  className?: string;
  /** Whether the action should be visible on mobile viewports (default: true) */
  visibleOnMobile?: boolean;
  /** Whether the action should be visible on desktop viewports (default: true) */
  visibleOnDesktop?: boolean;
  /** With `alwaysShowActions`, move this action into the mobile overflow (⋮)
   *  menu instead of rendering it as an inline mobile button. Desktop is unaffected. */
  mobileOverflow?: boolean;
}

export interface HeaderActionsMenuProps extends TestIdProps {
  /** Actions rendered inside the overflow dropdown. */
  actions: NavAction[];
  /** Accessible label for the trigger button. @default 'Actions' */
  label?: string;
  /** Extra class names applied to the wrapper. */
  className?: string;
  /** Dropdown alignment. @default 'bottom end' */
  align?: React.ComponentProps<typeof Dropdown>['align'];
}

/**
 * HeaderActionsMenu — kebab (⋮) overflow menu for the actions that are
 * visible on mobile. Returns `null` when there are no mobile-visible actions.
 */
export function HeaderActionsMenu({
  actions,
  label = 'Actions',
  className,
  align = 'bottom end',
  testId
}: HeaderActionsMenuProps) {
  const visibleActions = actions.filter(action => action.visibleOnMobile !== false);
  if (visibleActions.length === 0) return null;

  return (
    <div className={cn('flex', className)}>
      <Dropdown
        triggerButton={
          <Button
            outline='default'
            size='sm'
            aria-label={label}
            testId={testId}
            className='shrink-0 size-8 rounded-[10px] bg-background-input border border-border-base'
          >
            <Icon name='ellipsis-vertical' size='sm' />
          </Button>
        }
        align={align}
        size='sm'
      >
        {visibleActions.map(action => (
          <Dropdown.Item
            key={action.label}
            icon={action.icon}
            onClick={action.onClick}
            disabled={action.disabled}
            hint={action.tooltip}
            className={action.className}
          >
            {action.label}
          </Dropdown.Item>
        ))}
      </Dropdown>
    </div>
  );
}

export interface HeaderBackButtonProps extends TestIdProps {
  /** Callback fired when the button is clicked */
  onBack: () => void;
  /** Accessible label (used as aria-label) */
  backLabel: string;
  /** Optional visible text next to the arrow. When omitted only the icon is shown. */
  backText?: string;
  /** Extra class names applied to the button */
  className?: string;
}

/** The back arrow of `HeaderNavigation`, for a page that places it outside the bar. */
export const HeaderBackButton = React.forwardRef<HTMLButtonElement, HeaderBackButtonProps>(
  ({ onBack, backLabel, backText, className, testId }, ref) => (
    <Button
      ref={ref}
      outline='default'
      size='sm'
      onClick={onBack}
      aria-label={backLabel}
      testId={testId}
      className={cn(
        'shrink-0 rounded-[10px] bg-background-input',
        backText ? 'px-sm py-xs h-8' : 'size-8 p-sm',
        className
      )}
    >
      {backText ? (
        <span className='flex items-center gap-md'>
          <Icon name='arrow-left' size='sm' />
          <span className='text-sm'>{backText}</span>
        </span>
      ) : (
        <Icon name='arrow-left' size='sm' />
      )}
    </Button>
  )
);

HeaderBackButton.displayName = 'HeaderBackButton';

export interface HeaderNavigationProps extends BaseHeaderNavigationAttributes, TestIdProps {
  /** Page or document title displayed next to the back button */
  title?: string;
  /** Tags/badges displayed after the title (e.g. Badge components) */
  tags?: React.ReactNode;
  /** Subtitle displayed on the right side */
  subtitle?: string | React.ReactNode;
  /** Additional content rendered on the right side (e.g. action buttons) */
  children?: React.ReactNode;
  /** Callback fired when the back button is clicked */
  onBack?: () => void;
  /** Accessible label for the back button (used as aria-label) */
  backLabel?: string;
  /** Optional visible text shown next to the back arrow. When omitted only the icon is shown. */
  backText?: string;
  /**
   * Actions displayed in a dropdown menu on mobile viewports (< lg).
   * When provided, `children` are hidden on mobile and replaced by an overflow menu.
   */
  actions?: NavAction[];
  /** Accessible label for the mobile overflow menu trigger button. @default 'Actions' */
  mobileDropdownLabel?: string;
  /** When true, the mobile overflow menu is not rendered here (the page renders it elsewhere). */
  hideMobileActions?: boolean;
  /** When true, desktop action buttons are also shown on mobile as vertical buttons. @default false */
  alwaysShowActions?: boolean;
  /** When true, the back button is shown below `lg` only (the page renders it elsewhere). */
  hideBackOnDesktop?: boolean;
}

/**
 * HeaderNavigation — reusable navigation bar for detail pages.
 *
 * Renders a back button, optional title with tags on the left,
 * and optional subtitle with action buttons on the right.
 * Intended to be placed inside `<Page.Header>`.
 *
 * @example
 * ```tsx
 * <Page.Header>
 *   <HeaderNavigation
 *     title="FP24-0234.pdf"
 *     tags={<><Badge>Faktura</Badge> <Badge color="warning">Vyžaduje kontrolu</Badge></>}
 *     subtitle="22.12.2025 10:30:00"
 *     onBack={() => navigate(-1)}
 *   >
 *     <Button><Icon name="arrows-rotate" /> Reprocess</Button>
 *     <Button outline="default"><Icon name="download" /> Export</Button>
 *     <Button variant="error"><Icon name="trash-can" /> Smazat</Button>
 *   </HeaderNavigation>
 * </Page.Header>
 * ```
 */
export const HeaderNavigation = React.forwardRef<HTMLElement, HeaderNavigationProps>(
  (
    {
      title,
      tags,
      subtitle,
      children,
      onBack,
      backLabel = 'Back',
      backText,
      actions,
      mobileDropdownLabel = 'Actions',
      hideMobileActions = false,
      alwaysShowActions = false,
      hideBackOnDesktop = false,
      className,
      testId,
      ...props
    },
    ref
  ) => {
    const hasSecondRow = tags || subtitle;

    const visibleDesktopActions =
      actions?.filter(action => action.visibleOnDesktop !== false) ?? [];
    const visibleMobileActions = actions?.filter(action => action.visibleOnMobile !== false) ?? [];
    const mobileOverflowActions = visibleMobileActions.filter(action => action.mobileOverflow);
    const hasVisibleDesktopActions = visibleDesktopActions.length > 0;
    const hasVisibleMobileActions = visibleMobileActions.length > 0;

    return (
      <nav
        ref={ref}
        data-testid={testId}
        aria-label='Header navigation'
        className={cn('flex flex-col w-full pt-xs pb-md gap-sm lg:pt-0', className)}
        {...props}
      >
        {/* Row 1: Back button + Title + (desktop: tags, timestamp, actions) / (mobile: overflow menu) */}
        <div className='flex items-center justify-between gap-md'>
          {/* Left: Back button + Title + Desktop tags */}
          <div
            className={cn(
              'flex items-center gap-md min-w-0',
              !onBack && !title && !tags ? 'hidden lg:flex' : ''
            )}
          >
            {onBack && (
              <HeaderBackButton
                onBack={onBack}
                backLabel={backLabel}
                backText={backText}
                testId={testId && `${testId}-back`}
                className={hideBackOnDesktop ? 'lg:hidden' : undefined}
              />
            )}

            {title && <span className='text-lg pt-sm font-semibold truncate'>{title}</span>}

            {/* Desktop only: tags inline after title */}
            {tags && <div className='hidden lg:flex items-center gap-sm'>{tags}</div>}
          </div>

          {/* Right side of row 1 */}
          <div
            className={cn(
              'flex items-center gap-md shrink-0',
              alwaysShowActions ? 'grow lg:grow-0' : ''
            )}
          >
            {/* Desktop: subtitle + divider + action buttons */}
            {subtitle && (
              <div className='hidden lg:block'>
                {typeof subtitle === 'string' ? (
                  <span className='text-sm text-foreground-muted'>{subtitle}</span>
                ) : (
                  subtitle
                )}
              </div>
            )}

            {subtitle && (children || hasVisibleDesktopActions) && (
              <div className='hidden lg:block h-6 border-l border-border-base' aria-hidden='true' />
            )}

            {(hasVisibleDesktopActions || children) && (
              <div
                className={cn(
                  'flex items-center gap-sm',
                  alwaysShowActions ? 'w-full lg:w-auto' : '',
                  hasVisibleMobileActions && !alwaysShowActions ? 'hidden lg:flex!' : ''
                )}
              >
                {visibleDesktopActions.map(action => (
                  <React.Fragment key={action.label}>
                    {/* Mobile: horizontal icon + label — only when alwaysShowActions, visibleOnMobile, and not flagged for the overflow menu */}
                    {alwaysShowActions &&
                      action.visibleOnMobile !== false &&
                      !action.mobileOverflow && (
                        <Button
                          icon={action.icon}
                          onClick={action.onClick}
                          disabled={action.disabled}
                          title={action.tooltip}
                          variant='default'
                          outline='default'
                          size='sm'
                          aria-label={action.label}
                          data-onboarding-mobile={action.label}
                          className={cn(
                            'bg-background-input border-2 font-semibold flex-1 flex justify-center gap-sm lg:hidden',
                            action.className
                          )}
                        >
                          {action.label}
                        </Button>
                      )}
                    {/* Desktop: horizontal icon + label */}
                    <Button
                      icon={action.icon}
                      onClick={action.onClick}
                      disabled={action.disabled}
                      title={action.tooltip}
                      variant='default'
                      outline='default'
                      size='sm'
                      aria-label={action.label}
                      data-desktop-action={action.label}
                      className={cn(
                        'bg-background-input border-2 font-semibold',
                        alwaysShowActions ? 'hidden lg:flex' : 'flex',
                        action.className
                      )}
                    >
                      {action.label}
                    </Button>
                  </React.Fragment>
                ))}
                {children}

                {/* Mobile: overflow (⋮) menu for actions flagged `mobileOverflow` */}
                {alwaysShowActions && mobileOverflowActions.length > 0 && (
                  <HeaderActionsMenu
                    actions={mobileOverflowActions}
                    label={mobileDropdownLabel}
                    className='lg:hidden'
                    testId={testId && `${testId}-mobile-actions`}
                  />
                )}
              </div>
            )}

            {/* Mobile: overflow dropdown (compact mode — when actions aren't shown as inline buttons) */}
            {hasVisibleMobileActions && !hideMobileActions && !alwaysShowActions && actions && (
              <HeaderActionsMenu
                actions={actions}
                label={mobileDropdownLabel}
                className='lg:hidden'
                testId={testId && `${testId}-mobile-actions`}
              />
            )}
          </div>
        </div>

        {/* Row 2 on mobile only: tags + subtitle */}
        {hasSecondRow && (
          <div className={cn('flex lg:hidden items-center gap-md', onBack ? 'pl-11' : '')}>
            {tags && <div className='flex items-center gap-sm flex-wrap'>{tags}</div>}
            {subtitle && (
              <>
                {typeof subtitle === 'string' ? (
                  <span className='text-sm text-foreground-muted'>{subtitle}</span>
                ) : (
                  subtitle
                )}
              </>
            )}
          </div>
        )}
      </nav>
    );
  }
);

HeaderNavigation.displayName = 'HeaderNavigation';
